import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  Target, 
  GraduationCap, 
  Sparkles, 
  Phone, 
  Mail, 
  Calendar, 
  Clock, 
  Users, 
  HelpCircle,
  Brain,
  ShieldCheck,
  Send,
  Heart,
  FileDown,
  Eye,
  X,
  FileText,
  Layers,
  Sparkle, 
  MessageSquareQuote, 
  SlidersHorizontal,
  Briefcase,
  HeartHandshake
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../data';

const SkaermenHjernenOgTrivselen = () => {
  const [activeModalPoster, setActiveModalPoster] = useState(null); // null | 'elever' | 'foraeldre'
  
  const [formData, setFormData] = useState({
    oplaeg_type: 'Skærmen, hjernen & trivslen (7.-10. klasse)',
    package_choice: 'Plus (Anbefalet)',
    school_name: '',
    contact_name: '',
    contact_role: '',
    email: '',
    phone: '',
    grade_levels: '',
    student_count: '',
    preferred_period: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [errorMessage, setErrorMessage] = useState('');

  const flyerEleverUrl = "https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/ska-rmen-hjernen-trivslen.png";
  const flyerForaeldreUrl = "https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/hjernen-barnet-mobilen.png";
  const priserPdfUrl = "https://assets.auxodev.dk/assets/a0e52287-3b67-46d1-99f1-643069115c1c/priser.pdf";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    // Antispam honeypot
    if (formData.website_url) {
      setIsSubmitting(false);
      setSubmitStatus('success');
      return;
    }

    try {
      const response = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          form_name: 'Forespørgsel: Skoleoplæg - Trivlogi',
          ...formData,
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          oplaeg_type: 'Skærmen, hjernen & trivslen (7.-10. klasse)',
          package_choice: 'Plus (Anbefalet)',
          school_name: '',
          contact_name: '',
          contact_role: '',
          email: '',
          phone: '',
          grade_levels: '',
          student_count: '',
          preferred_period: '',
          message: '',
          website_url: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setSubmitStatus('error');
      setErrorMessage(err.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring direkte.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 1. Elevoplægget indhold
  const elevIndsigt = [
    "Hvordan skærmbrug og algoritmer biologisk påvirker hjernen",
    "Dopaminens bagside og jerngreb i belønningssystemet",
    "Hvorfor sociale medier kan skabe mistrivsel, FOMO og sammenligningspres",
    "Hvordan man kan styrke sit fokus, nærvær og sin nattesøvn",
    "Hvad man selv kan gøre for en sundere digital hverdag uden forbud",
    "Perspektiver på fællesskab, identitet og mental sundhed"
  ];

  // 2. Forældreoplægget indhold
  const foraeldreIndsigt = [
    "Hvad der sker i barnets hjerne, når det bruger mobil, sociale medier og gaming",
    "Hvorfor digitale mekanismer er så dragende for en hjerne under udvikling",
    "Hvordan søvn, opmærksomhed og trivsel påvirkes i hverdagen",
    "Hvordan man som forælder støtter sit barn uden daglig konflikt og kamp om skærmen",
    "Konkrete strategier og familieaftaler, der rent faktisk virker i praksis",
    "Hvordan man gradvist kan hjælpe barnet til mere ansvar og selvregulering",
    "Hvad man kan gøre som forælder, hvis noget online gør barnet utrygt"
  ];

  // Pakker baseret på priser.pdf
  const packages = [
    {
      id: "basis",
      name: "Basis",
      price: "6.500 kr.",
      tag: "Det fokuserede oplæg",
      popular: false,
      features: [
        "60 – 75 min. oplæg",
        "Skærm, sociale medier og dopamin",
        "Biologi → Mindset → Strategi",
        "14-dages challenge med materiale til deltagerne",
        "Kort spørgerunde",
        "Relevant materiale til målgruppen"
      ],
      ctaText: "Vælg Basis"
    },
    {
      id: "plus",
      name: "Plus",
      price: "9.500 kr.",
      tag: "Det udvidede forløb",
      popular: true,
      badge: "Anbefalet",
      features: [
        "75 – 90 min. udvidet oplæg",
        "Mere dialog, refleksion og spørgetid",
        "Relevant materiale til deltagere og fagpersoner",
        "14-dages challenge med materiale til deltagerne",
        "Kort opfølgning efter oplægget"
      ],
      ctaText: "Vælg Plus (Anbefalet)"
    },
    {
      id: "skolepakken",
      name: "Skolepakken",
      price: "14.500 kr.",
      tag: "Når skolen skal have mere med",
      popular: false,
      features: [
        "To oplæg samme dag",
        "Eller: oplæg + 60 min. session for personale",
        "Relevant materiale til målgruppen",
        "14-dages challenge med materiale til deltagerne",
        "Opfølgende sparring"
      ],
      ctaText: "Vælg Skolepakken"
    }
  ];

  const handleSelectPackage = (pkgName) => {
    setFormData(prev => ({ ...prev, package_choice: pkgName }));
    const element = document.getElementById('book-oplaeg');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-white">
      {/* 1. Hero Section */}
      <section className="relative bg-zinc-50 overflow-hidden min-h-[500px] lg:min-h-[650px] flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10 py-16 lg:py-0">
          <div className="grid lg:grid-cols-2 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:max-w-xl lg:pr-12"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zinc-900 mb-8 leading-tight">
                Skærmen, hjernen og trivslen
              </h1>

              <p className="text-xl text-slate-600 mb-6 leading-relaxed">
                PISA 2025 har sat fokus på de unges læring og trivsel. Men hvad sker der egentlig i hjernen, når mobilen konstant bipper? Vi tilbyder to målrettede oplæg baseret på biologisk viden i øjenhøjde – helt uden løftede pegefingre.
              </p>
              
              <p className="text-lg text-emerald-800/80 mb-10 leading-relaxed italic border-l-4 border-emerald-800 pl-6 py-2 bg-emerald-50/30">
                "Det handler ikke om forbud, men om biologisk viden i øjenhøjde."
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#de-to-oplaeg"
                  className="bg-emerald-900 text-white px-8 py-4 font-bold text-lg hover:bg-emerald-950 transition-all flex items-center group shadow-md"
                >
                  Se de to oplæg
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#tilpasning-og-maalgrupper"
                  className="bg-white text-zinc-800 border border-zinc-300 hover:border-emerald-800 px-8 py-4 font-bold text-lg hover:bg-emerald-50/40 transition-all flex items-center shadow-xs"
                >
                  Målgrupper &amp; tilpasning
                </a>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Hero Image */}
        <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2 h-[400px] lg:h-full bg-zinc-100 overflow-hidden">
          <motion.div
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
            className="w-full h-full relative"
          >
            <img
              src="/materiale-260501/image00009.jpg"
              alt="Michael Lunde underviser ved tavle"
              className="w-full h-full object-cover"
            />
            {/* Subtle Overlay to blend with text on smaller screens if needed */}
            <div className="absolute inset-0 bg-gradient-to-r from-zinc-50/20 via-transparent to-transparent lg:hidden" />
            
            {/* Decorative element */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-900/10 -translate-y-12 translate-x-12 rotate-45 hidden lg:block" />
            <div className="absolute bottom-0 right-0 w-full h-1/4 bg-gradient-to-t from-emerald-950/20 to-transparent pointer-events-none" />
          </motion.div>
        </div>
      </section>

      {/* 2. Top Sektion (Målgruppetilpasning først jf. Michaels ønske): Oplægget kan tilpasses jeres målgruppe */}
      <section id="tilpasning-og-maalgrupper" className="py-16 lg:py-20 bg-zinc-50 border-b border-zinc-200 scroll-mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-zinc-200 shadow-md relative overflow-hidden">
            {/* 2-kolonne layout: Indhold til venstre, CTA boks til højre */}
            <div className="grid lg:grid-cols-12 items-stretch">
              {/* Venstre kolonne: Tekst og målgruppegalleri */}
              <div className="lg:col-span-8 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-zinc-200">
                {/* Top header & intro */}
                <div className="p-8 sm:p-10 lg:p-10 border-b border-zinc-200">
                  <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4 tracking-tight leading-tight">
                    Oplægget kan tilpasses jeres målgruppe
                  </h2>

                  <p className="text-lg text-slate-700 leading-relaxed mb-6">
                    <strong>Skærmen, hjernen &amp; trivslen</strong> handler ikke kun om børn og unge. Vores digitale vaner påvirker os alle, og oplæggene kan tilpasses det arrangement, I står med – eksempelvis arbejdspladser, lærere, forældre eller foreninger.
                  </p>

                  {/* Fremhævet metode-boks */}
                  <div className="bg-zinc-50 border-l-4 border-emerald-800 p-5">
                    <div className="flex items-start gap-4">
                      <div className="bg-emerald-900 p-2 text-white shrink-0">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div>
                        <span className="block text-xs font-bold uppercase tracking-wider text-emerald-900 mb-1">
                          Biologi → Mindset → Strategi
                        </span>
                        <p className="text-sm sm:text-base text-zinc-800 leading-relaxed">
                          Vi tilpasser indhold, eksempler og redskaber, så deltagerne får konkret viden, de kan genkende sig selv i og omsætte i deres egen hverdag.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Målgruppe-gitter */}
                <div className="p-8 sm:p-10 lg:p-10 bg-white">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="bg-zinc-50 border border-zinc-100 p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="bg-emerald-900 p-2 w-fit mb-3 text-white">
                          <Briefcase className="w-4 h-4" />
                        </div>
                        <span className="block text-base font-bold text-zinc-900 mb-1.5">
                          Arbejdspladser
                        </span>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Fokus på uafbrudt arbejdstid, digital støj, pauser og mental restitution i en travl hverdag.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-zinc-200 text-xs font-bold text-emerald-900 uppercase tracking-wider">
                        Fokus &amp; energi
                      </div>
                    </div>

                    <div className="bg-zinc-50 border border-zinc-100 p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="bg-emerald-900 p-2 w-fit mb-3 text-white">
                          <GraduationCap className="w-4 h-4" />
                        </div>
                        <span className="block text-base font-bold text-zinc-900 mb-1.5">
                          Skoler &amp; fagfolk
                        </span>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Fælles fagligt fundament, pædagogisk praksis og konkrete samtalemodeller med eleverne.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-zinc-200 text-xs font-bold text-emerald-900 uppercase tracking-wider">
                        Pædagogisk praksis
                      </div>
                    </div>

                    <div className="bg-zinc-50 border border-zinc-100 p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="bg-emerald-900 p-2 w-fit mb-3 text-white">
                          <Users className="w-4 h-4" />
                        </div>
                        <span className="block text-base font-bold text-zinc-900 mb-1.5">
                          Forældre &amp; familier
                        </span>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Fælles klasseregler, forebyggelse af skærmkonflikter og gode digitale rammer i hjemmet.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-zinc-200 text-xs font-bold text-emerald-900 uppercase tracking-wider">
                        Samarbejde i hjemmet
                      </div>
                    </div>

                    <div className="bg-zinc-50 border border-zinc-100 p-5 shadow-xs flex flex-col justify-between">
                      <div>
                        <div className="bg-emerald-900 p-2 w-fit mb-3 text-white">
                          <HeartHandshake className="w-4 h-4" />
                        </div>
                        <span className="block text-base font-bold text-zinc-900 mb-1.5">
                          Foreninger &amp; klubber
                        </span>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          Idrætsforeninger og fællesskaber, hvor nærvær, trivsel og analogt samvær skal styrkes.
                        </p>
                      </div>
                      <div className="mt-4 pt-2.5 border-t border-zinc-200 text-xs font-bold text-emerald-900 uppercase tracking-wider">
                        Nærvær i fællesskabet
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Højre kolonne: CTA-boks */}
              <div className="lg:col-span-4 bg-zinc-900 text-white p-8 sm:p-12 lg:p-12 flex flex-col justify-between relative overflow-hidden">
                <div className="relative z-10">
                  <span className="text-emerald-400 font-bold uppercase tracking-wider text-xs block mb-3">
                    Uforpligtende samtale
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-snug">
                    Har I en anden målgruppe i tankerne?
                  </h3>
                  <p className="text-slate-300 text-base leading-relaxed mb-6">
                    <strong>Book en gratis og uforpligtende samtale</strong>, hvor vi sammen kan tale om jeres behov, målgruppe og arrangement – og hvordan oplægget kan tilpasses, så det skaber størst mulig værdi for netop jer.
                  </p>

                  <div className="p-5 bg-white/5 border border-white/10 rounded-sm mb-8 space-y-3">
                    <div className="flex items-center gap-3 text-sm text-slate-200">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                      <span>Ingen binding eller forpligtelse</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-200">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                      <span>Sparring direkte med Michael Lunde</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-200">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                      <span>Konkrete forslag til jeres arrangement</span>
                    </div>
                  </div>
                </div>

                <div className="relative z-10 flex flex-col gap-3 pt-6 border-t border-white/10">
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({ 
                        ...prev, 
                        oplaeg_type: 'Anden målgruppe (virksomhed, personale, forening)' 
                      }));
                      document.getElementById('book-oplaeg')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full bg-emerald-900 text-white px-8 py-4 font-bold text-base hover:bg-emerald-950 transition-all flex items-center justify-center group shadow-md cursor-pointer whitespace-nowrap"
                  >
                    <span>Book en gratis samtale</span>
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </button>
                  <a
                    href="#de-to-oplaeg"
                    className="w-full bg-white/10 hover:bg-white/15 text-white font-bold text-base px-6 py-3.5 transition-colors border border-white/20 flex items-center justify-center gap-2 whitespace-nowrap text-center text-sm"
                  >
                    <span>Se de to faste oplæg ↓</span>
                  </a>
                </div>

                {/* Subtil dekorativ baggrundsaccent */}
                <div className="absolute -bottom-24 -right-24 w-64 h-64 bg-emerald-900/10 rounded-full blur-3xl pointer-events-none" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2b. De to faste oplæg: Elevoplæg og Forældreoplæg */}
      <section id="de-to-oplaeg" className="py-16 bg-white border-b border-zinc-200 scroll-mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              To gennemarbejdede oplæg – klar til booking
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed">
              Herunder finder I vores to mest efterspurgte standardoplæg: ét til udskolingseleverne og ét til forældrene på mellemtrinnet. Begge kan bookes enkeltvis, kombineres i Skolepakken eller tilpasses jeres behov.
            </p>
          </div>

          {/* De to oplægs-kort side om side */}
          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            
            {/* Oplæg 1: Elever */}
            <div 
              id="elevoplaeg"
              className="bg-white border border-zinc-200 p-7 sm:p-9 shadow-xs flex flex-col justify-between hover:shadow-md hover:border-emerald-200 transition-all relative rounded-none"
            >
              <div className="absolute top-0 right-0 bg-emerald-900 text-white text-[11px] font-bold px-3.5 py-1.5 uppercase tracking-wider">
                Elevoplæg
              </div>

              <div>
                {/* Header */}
                <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
                  <GraduationCap className="w-5 h-5 text-emerald-800" />
                  <span className="text-xs uppercase tracking-wider font-bold">Udskoling · 7. – 10. klasse</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-2">
                  Skærmen, hjernen & trivslen
                </h3>

                <p className="text-slate-600 mb-6 text-sm sm:text-base leading-relaxed">
                  Et tankevækkende og praksisnært elevoplæg om, hvordan mobiler, sociale medier og algoritmer påvirker unges hjerner, fokus og trivsel – og hvad de selv kan gøre.
                </p>

                {/* Key metadata pills */}
                <div className="grid grid-cols-2 gap-3 p-3.5 bg-zinc-50 border border-zinc-200/80 mb-6 text-xs sm:text-sm">
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Varighed</span>
                    <span className="font-semibold text-zinc-900">60 – 90 minutter</span>
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Målgruppe</span>
                    <span className="font-semibold text-zinc-900">7. – 10. klasse</span>
                  </div>
                </div>

                {/* Indsigtspunkter */}
                <div className="mb-6">
                  <h4 className="text-xs uppercase tracking-wider font-bold text-zinc-900 mb-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-800 rounded-full inline-block"></span>
                    Eleverne får bl.a. indsigt i:
                  </h4>
                  <ul className="space-y-2.5">
                    {elevIndsigt.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 mr-2.5 mt-0.5 shrink-0" />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 14 dages challenge */}
                <div className="bg-emerald-50/80 border-l-3 border-emerald-800 p-3.5 mb-6 text-xs">
                  <span className="font-bold block text-emerald-950 uppercase tracking-wider text-[11px] mb-1">
                    Inkl. 14-dages elev-challenge
                  </span>
                  <p className="text-slate-700 leading-relaxed">
                    Frivilligt eksperiment, hvor eleverne afprøver små justeringer og mærker forskellen på egen krop: søvn, energi og fokus.
                  </p>
                </div>

                {/* Citat */}
                <blockquote className="bg-zinc-50/80 p-3.5 border-l-2 border-zinc-300 text-xs italic text-slate-600 mb-6">
                  "Et enormt relevant oplæg, som skabte refleksion og gode samtaler blandt vores elever."
                  <footer className="not-italic font-bold text-zinc-900 mt-1">— Skoleleder</footer>
                </blockquote>
              </div>

              {/* Handlingsknapper / footer */}
              <div className="pt-4 border-t border-zinc-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveModalPoster('elever')}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 border border-emerald-200 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5 text-emerald-800" />
                    <span>Se flyer</span>
                  </button>
                  <a
                    href={flyerEleverUrl}
                    download="Skaermen-hjernen-og-trivslen.png"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-700 bg-white hover:bg-zinc-50 px-3 py-2 border border-zinc-200 transition-colors"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span>Download</span>
                  </a>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, oplaeg_type: 'Skærmen, hjernen & trivslen (7.-10. klasse)' }));
                    document.getElementById('book-oplaeg')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1 text-sm font-bold text-emerald-900 hover:text-emerald-700 cursor-pointer self-start sm:self-auto"
                >
                  Forespørg på elevoplæg <ArrowRight className="w-4 h-4 ml-0.5" />
                </button>
              </div>
            </div>

            {/* Oplæg 2: Forældre */}
            <div 
              id="foraeldreoplaeg"
              className="bg-white border border-zinc-200 p-7 sm:p-9 shadow-xs flex flex-col justify-between hover:shadow-md hover:border-emerald-200 transition-all relative rounded-none"
            >
              <div className="absolute top-0 right-0 bg-emerald-900 text-white text-[11px] font-bold px-3.5 py-1.5 uppercase tracking-wider">
                Forældreoplæg
              </div>

              <div>
                {/* Header */}
                <div className="flex items-center gap-2 text-emerald-800 font-semibold mb-2">
                  <Users className="w-5 h-5 text-emerald-800" />
                  <span className="text-xs uppercase tracking-wider font-bold">Mellemtrin · 3. – 6. klasse</span>
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-1">
                  Hjernen, barnet & mobilen
                </h3>
                <p className="text-sm font-bold text-emerald-800 mb-2">
                  Når mobilen flytter ind
                </p>

                <p className="text-slate-600 mb-6 text-sm sm:text-base leading-relaxed">
                  Forældreoplæg om børns digitale hverdag. Giver viden om barnets hjerne, og hvordan man som voksne støtter sunde skærmvaner uden konflikt.
                </p>

                {/* Key metadata pills */}
                <div className="grid grid-cols-2 gap-3 p-3.5 bg-zinc-50 border border-zinc-200/80 mb-6 text-xs sm:text-sm">
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Varighed</span>
                    <span className="font-semibold text-zinc-900">90 minutter</span>
                  </div>
                  <div>
                    <span className="block text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Målgruppe</span>
                    <span className="font-semibold text-zinc-900">Forældre (3. – 6. klasse)</span>
                  </div>
                </div>

                {/* Indsigtspunkter */}
                <div className="mb-6">
                  <h4 className="text-xs uppercase tracking-wider font-bold text-zinc-900 mb-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-800 rounded-full inline-block"></span>
                    Forældrene får bl.a. indsigt i:
                  </h4>
                  <ul className="space-y-2.5">
                    {foraeldreIndsigt.map((item, idx) => (
                      <li key={idx} className="flex items-start text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 mr-2.5 mt-0.5 shrink-0" />
                        <span className="leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 14 dages challenge */}
                <div className="bg-emerald-50/80 border-l-3 border-emerald-800 p-3.5 mb-6 text-xs">
                  <span className="font-bold block text-emerald-950 uppercase tracking-wider text-[11px] mb-1">
                    Inkl. 14-dages familie-challenge
                  </span>
                  <p className="text-slate-700 leading-relaxed">
                    Gennemarbejdet familie-challenge med konkrete, enkle aftaler og vaner, der skaber ro og fælles fodslag i hjemmet.
                  </p>
                </div>

                {/* Citat */}
                <blockquote className="bg-zinc-50/80 p-3.5 border-l-2 border-zinc-300 text-xs italic text-slate-600 mb-6">
                  "En balanceret og konkret tilgang, som giver mening i hverdagen. Vi følte os klædt på – ikke dømt."
                  <footer className="not-italic font-bold text-zinc-900 mt-1">— Forælder, 4. klasse</footer>
                </blockquote>
              </div>

              {/* Handlingsknapper / footer */}
              <div className="pt-4 border-t border-zinc-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveModalPoster('foraeldre')}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-900 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 border border-emerald-200 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5 text-emerald-800" />
                    <span>Se flyer</span>
                  </button>
                  <a
                    href={flyerForaeldreUrl}
                    download="Hjernen-barnet-og-mobilen.png"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-zinc-700 bg-white hover:bg-zinc-50 px-3 py-2 border border-zinc-200 transition-colors"
                    title="Download flyer for Hjernen, barnet & mobilen"
                  >
                    <FileDown className="w-3.5 h-3.5" />
                    <span>Download</span>
                  </a>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({ ...prev, oplaeg_type: 'Hjernen, barnet & mobilen (3.-6. klasse)' }));
                    document.getElementById('book-oplaeg')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-1 text-sm font-bold text-emerald-900 hover:text-emerald-700 cursor-pointer self-start sm:self-auto"
                >
                  Forespørg på forældreoplæg <ArrowRight className="w-4 h-4 ml-0.5" />
                </button>
              </div>
            </div>

          </div>

          {/* Fleksibilitetsnote i bunden af de to faste oplæg */}
          <div className="mt-12 bg-zinc-50 border border-zinc-200 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="w-5 h-5 text-emerald-800 shrink-0" />
              <p className="text-sm text-slate-700">
                <strong>Skal forløbet tilpasses en temadag, workshop eller personalegruppe?</strong> Vi sammensætter gerne programmet, så det matcher jeres målgruppe, tidsramme og faglige ønsker.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setFormData(prev => ({ ...prev, oplaeg_type: 'Anden målgruppe (virksomhed, personale, forening)' }));
                document.getElementById('book-oplaeg')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-xs font-bold uppercase tracking-wider text-emerald-900 hover:text-emerald-700 whitespace-nowrap inline-flex items-center gap-1 cursor-pointer shrink-0"
            >
              Forhør om tilpasset forløb <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* 3. Fælles fagligt fundament: Biologi → Mindset → Strategi */}
      <section className="py-20 bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-400 font-bold uppercase tracking-wider text-xs block mb-2">
                Den faglige metode bag Trivlogi®
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Biologi → Mindset → Strategi
              </h2>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                Uanset om målgruppen er udskolingselever eller forældre til mellemtrinnet, bygger begge oplæg på samme gennemprøvede model. Vi arbejder med naturen – ikke imod den:
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-5 bg-white/5 border-l-4 border-emerald-500">
                  <div className="bg-emerald-900 p-2.5 text-white shrink-0">
                    <Brain className="w-5 h-5 text-emerald-300" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-base mb-1">1. Biologi (Forståelse)</h4>
                    <p className="text-sm text-slate-300">
                      Hvad sker der rent neurologisk i belønningssystemet og med dopamin, når skærmen konstant kalder? Når vi forstår biologien, forsvinder skyld og skam.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-white/5 border-l-4 border-emerald-500">
                  <div className="bg-emerald-900 p-2.5 text-white shrink-0">
                    <Heart className="w-5 h-5 text-emerald-300" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-base mb-1">2. Mindset (Nye perspektiver)</h4>
                    <p className="text-sm text-slate-300">
                      Fra kontrol til forståelse. Vi undersøger de automatiske mønstre, frygten for at gå glip af noget (FOMO) og det sammenligningspres, mange unge oplever.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 bg-white/5 border-l-4 border-emerald-500">
                  <div className="bg-emerald-900 p-2.5 text-white shrink-0">
                    <Target className="w-5 h-5 text-emerald-300" aria-hidden="true" />
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-base mb-1">3. Strategi (Handling i hverdagen)</h4>
                    <p className="text-sm text-slate-300">
                      Konkrete redskaber, miljødesign og familieaftaler, der virker i virkeligheden uden at belaste viljestyrken unødigt.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white p-8 border border-emerald-900/40 shadow-xl flex items-center justify-center">
                <img 
                  src="/materiale-260428/Bio-mind-strat_gron2.svg" 
                  alt="Biologi Mindset Strategi model" 
                  className="w-full max-w-md mx-auto"
                />
              </div>
              <p className="text-center text-xs text-emerald-200/80 mt-4 italic">
                Modellen danner fundamentet for både elevoplæg, forældremøder og personaleforløb.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Pakker og priser (Erstatter de gamle 3 kasser) */}
      <section id="priser" className="py-20 bg-zinc-50 border-t border-zinc-200 scroll-mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Vælg den løsning, der passer til jeres skole
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed max-w-2xl mx-auto [text-wrap:balance]">
              Pakkerne herunder gælder for både elevoplægget <em>"Skærmen, hjernen & trivslen"</em> og forældreoplægget <em>"Hjernen, barnet & mobilen"</em>. Indhold og eksempler tilpasses den konkrete målgruppe.
            </p>

            <div className="mt-4 flex items-center justify-center gap-2">
              <a 
                href={priserPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-bold text-emerald-900 bg-white hover:bg-emerald-50 px-3 py-1.5 border border-zinc-300 shadow-2xs"
              >
                <FileText className="w-3.5 h-3.5 text-emerald-800" />
                <span>Åbn prisoversigt som PDF</span>
              </a>
            </div>
          </div>

          {/* 3 Prispakker (Basis, Plus, Skolepakken) */}
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {packages.map((pkg) => (
              <div 
                key={pkg.id} 
                className={`bg-white p-8 border flex flex-col justify-between transition-all relative ${
                  pkg.popular 
                    ? 'border-emerald-900 shadow-lg ring-2 ring-emerald-900/10' 
                    : 'border-zinc-200 shadow-sm hover:shadow-md'
                }`}
              >
                {pkg.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-900 text-white text-xs font-bold px-3 py-1 uppercase tracking-wider rounded-xs shadow-xs">
                    {pkg.badge}
                  </div>
                )}

                <div>
                  <div className="mb-4">
                    <span className="text-xs uppercase tracking-wider font-bold text-slate-500">
                      {pkg.tag}
                    </span>
                    <h3 className="text-2xl font-bold text-zinc-900 mt-1">{pkg.name}</h3>
                  </div>

                  <div className="mb-6 pb-6 border-b border-zinc-100">
                    <div className="text-3xl sm:text-4xl font-extrabold text-emerald-950">
                      {pkg.price}
                    </div>
                    <span className="text-xs text-slate-500 font-medium">Ekskl. moms og transport</span>
                  </div>

                  <p className="text-xs uppercase tracking-wider font-bold text-zinc-900 mb-3">
                    Det indeholder:
                  </p>
                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feat, i) => (
                      <li key={i} className="flex items-start text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-700 mr-2.5 mt-0.5 shrink-0" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-zinc-100">
                  <button
                    type="button"
                    onClick={() => handleSelectPackage(`${pkg.name} (${pkg.price})`)}
                    className={`w-full py-3.5 px-4 font-bold text-sm text-center transition-colors cursor-pointer flex items-center justify-center gap-2 ${
                      pkg.popular
                        ? 'bg-emerald-900 text-white hover:bg-emerald-950 shadow-md'
                        : 'bg-zinc-100 hover:bg-emerald-900 hover:text-white text-zinc-800'
                    }`}
                  >
                    <span>{pkg.ctaText}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Særlig sektion om 14-dages challenge + Fleksibel tilpasning & Temadag/Workshop */}
          <div className="grid md:grid-cols-2 gap-8">
            
            {/* 14-dages challenge info */}
            <div className="bg-white p-6 sm:p-8 border border-zinc-200 shadow-sm flex items-start gap-4">
              <div className="bg-emerald-900 p-2.5 text-white shrink-0">
                <Calendar className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-zinc-900 mb-2">
                  14-dages challenge tilpasses oplægget
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  Uanset hvilken pakke I vælger, medfølger der en gennemarbejdet challenge med materiale til deltagerne:
                </p>
                <div className="space-y-1.5 text-xs text-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-emerald-900">Elevoplæg:</span>
                    <span>Elev-challenge med fokus på nattesøvn, pauser og personlige skærmvaner.</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-emerald-900">Forældreoplæg:</span>
                    <span>Familie-challenge med fokus på konkrete rammer og rolige zoner i hjemmet.</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Note om Temadag / Workshop / Fleksibilitet */}
            <div className="bg-emerald-50/60 p-6 sm:p-8 border border-emerald-200 shadow-sm flex items-start gap-4">
              <div className="bg-emerald-800 p-2.5 text-white shrink-0">
                <SlidersHorizontal className="w-6 h-6" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-emerald-950 mb-2">
                  Kan forløbet tilpasses temadage eller workshops?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">
                  <strong>Ja, absolut.</strong> Selvom temadage eller workshops ikke er en fast standardpakke, kan indholdet, tidsrammen og de praktiske øvelser altid tilpasses, hvis skolen ønsker et længere eller anderledes forløb for klasser eller personale.
                </p>
                <p className="text-xs text-emerald-900 font-semibold mt-2">
                  Tag en uforpligtende snak om, hvordan vi kan skræddersy det til jeres skema.
                </p>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 5. Benefits Grid - Hvad får skolen og eleverne ud af det? */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-zinc-900 mb-4 [text-wrap:balance]">Hvad får skolen, eleverne og forældrene ud af det?</h2>
            <p className="text-slate-600 max-w-xl mx-auto text-lg leading-relaxed [text-wrap:balance]">
              Målet er at skabe reel forståelse og handlekraft, så de unge trives bedre både fagligt og socialt.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-zinc-50 p-8 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-emerald-900 p-2 w-fit mb-4">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 mb-2">Fælles sprog for skærmvaner</h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Skolen og hjemmet får et neutralt og biologisk funderet sprog om dopamin og skærmbrug.
              </p>
            </div>

            <div className="bg-zinc-50 p-8 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-emerald-900 p-2 w-fit mb-4">
                <Clock className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 mb-2">Bedre søvn og restitution</h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Større bevidsthed om aftenvaner og det lys, der stjæler den dybe søvn og elevernes overskud.
              </p>
            </div>

            <div className="bg-zinc-50 p-8 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-emerald-900 p-2 w-fit mb-4">
                <Target className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 mb-2">Aflastning af viljestyrken</h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Konkrete miljøjusteringer og vaner, der fjerner presset fra viljestyrken og gør det nemt at lykkes.
              </p>
            </div>

            <div className="bg-zinc-50 p-8 border border-zinc-100 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-emerald-900 p-2 w-fit mb-4">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <h4 className="text-xl font-bold text-zinc-900 mb-2">Dialog uden forbud</h4>
              <p className="text-sm text-slate-600 leading-relaxed">
                Ingen fordømmelse eller pegefingre – kun åben nysgerrighed, psykologisk tryghed og ejerskab.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Udtalelser fra skoler & forældre */}
      <section className="py-20 bg-zinc-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <img 
            src="/materiale-260501/image00010.jpg" 
            alt="Møde i fællesskab" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-emerald-400 uppercase tracking-widest text-xs font-bold block mb-2">
              Tilbagemeldinger
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Hvad siger skoler, lærere og forældre?
            </h2>
            <p className="text-slate-300 text-base">
              Erfaringer fra afholdte forløb og oplæg rundt om i landet.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-xs p-8 border border-white/15 flex flex-col justify-between">
              <div>
                <MessageSquareQuote className="w-8 h-8 text-emerald-400 mb-4" />
                <p className="text-base text-emerald-50 leading-relaxed italic mb-6">
                  "Michael formår at gøre en kompleks problematik enkel, spændende og nærværende. Eleverne var fuldstændig fanget."
                </p>
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="font-bold text-white">Lærer i udskolingen</p>
                <p className="text-xs text-emerald-300">Skoleoplæg for 8. klasse</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xs p-8 border border-white/15 flex flex-col justify-between">
              <div>
                <MessageSquareQuote className="w-8 h-8 text-emerald-400 mb-4" />
                <p className="text-base text-emerald-50 leading-relaxed italic mb-6">
                  "En balanceret og konkret tilgang, som giver mening i hverdagen. Vi følte os klædt på som forældre – ikke dømt."
                </p>
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="font-bold text-white">Forælder</p>
                <p className="text-xs text-emerald-300">Forældreoplæg for 4. klasse</p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-xs p-8 border border-white/15 flex flex-col justify-between">
              <div>
                <MessageSquareQuote className="w-8 h-8 text-emerald-400 mb-4" />
                <p className="text-base text-emerald-50 leading-relaxed italic mb-6">
                  "Jeg tænker meget mere over, hvordan jeg bruger min mobil efter oplægget – og det er faktisk rigtig rart at vide, hvad der sker."
                </p>
              </div>
              <div className="border-t border-white/10 pt-4">
                <p className="font-bold text-white">Elev, 8. klasse</p>
                <p className="text-xs text-emerald-300">Deltager i 14-dages challenge</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Booking Form & Contact Section */}
      <section id="book-oplaeg" className="py-20 bg-zinc-50 scroll-mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Contact info column */}
            <div className="lg:col-span-5 space-y-8">
              <div>
                <span className="text-emerald-700 font-bold uppercase tracking-wider text-xs block mb-2">
                  Uforpligtende dialog
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 leading-tight mb-4">
                  Lad os tage en snak om jeres skole
                </h2>
                <p className="text-slate-600 text-lg leading-relaxed">
                  Jeg kommer meget gerne ud på skolen eller tager en online dialog om, hvordan oplægget bedst matches med jeres klassetrin, forældregruppe eller temadag.
                </p>
              </div>

              <div className="space-y-4">
                <a 
                  href="tel:26368306"
                  className="flex items-center p-5 bg-white border border-zinc-200 shadow-sm hover:border-emerald-800 transition-colors group"
                >
                  <div className="bg-emerald-900 p-3 text-white mr-4 group-hover:bg-emerald-950">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Direkte telefon</p>
                    <p className="text-xl font-bold text-zinc-900">26 36 83 06</p>
                  </div>
                </a>

                <a 
                  href="mailto:kontakt@ditlivditansvar.dk"
                  className="flex items-center p-5 bg-white border border-zinc-200 shadow-sm hover:border-emerald-800 transition-colors group"
                >
                  <div className="bg-emerald-900 p-3 text-white mr-4 group-hover:bg-emerald-950">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">E-mail</p>
                    <p className="text-lg font-bold text-zinc-900">kontakt@ditlivditansvar.dk</p>
                  </div>
                </a>
              </div>

              <div className="p-6 bg-white border border-zinc-200 shadow-sm">
                <h4 className="font-bold text-zinc-900 mb-2">Hvad sker der efter jeres henvendelse?</h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span>Jeg kontakter jer inden for 24-48 timer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span>Vi afstemmer klassetrin, datoer og praktiske rammer</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 shrink-0" />
                    <span>I modtager en kort visuel oversigt til lærerteamet eller forældre</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Form column */}
            <div className="lg:col-span-7">
              <div className="bg-white p-8 md:p-12 border border-zinc-200 shadow-xl">
                <h3 className="text-2xl font-bold text-zinc-900 mb-2">
                  Forespørg på oplæg til skolen
                </h3>
                <p className="text-slate-600 mb-8 text-sm">
                  Udfyld formularen herunder, så vender jeg hurtigt tilbage med forslag til dato og tilpasning.
                </p>

                {submitStatus === 'success' && (
                  <div className="mb-8 p-6 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded">
                    <h4 className="font-bold text-lg mb-1 flex items-center gap-2">
                      <CheckCircle2 className="h-5 w-5 text-emerald-700" />
                      Tak for din henvendelse!
                    </h4>
                    <p className="text-sm">
                      Jeg har modtaget din forespørgsel og vender tilbage hurtigst muligt. Du er også altid velkommen til at ringe på tlf. 26 36 83 06.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mb-8 p-6 bg-red-50 border border-red-200 text-red-900 rounded">
                    <p className="font-semibold text-sm">{errorMessage}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Antispam honeypot */}
                  <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                    <label htmlFor="school_website_url">Hjemmeside url (lad stå tom)</label>
                    <input
                      type="text"
                      id="school_website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Oplæg valg */}
                  <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                      Hvilket oplæg er I interesseret i? *
                    </label>
                    <select
                      name="oplaeg_type"
                      value={formData.oplaeg_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900 font-medium"
                    >
                      <option value="Skærmen, hjernen & trivslen (7.-10. klasse)">
                        Elevoplæg: Skærmen, hjernen & trivslen (7. – 10. klasse)
                      </option>
                      <option value="Hjernen, barnet & mobilen (3.-6. klasse)">
                        Forældreoplæg: Hjernen, barnet & mobilen (3. – 6. klasse)
                      </option>
                      <option value="Begge oplæg (Skolepakken)">
                        Skolepakken: Både elevoplæg og forældre/personale
                      </option>
                      <option value="Anden målgruppe (virksomhed, personale, forening)">
                        Anden målgruppe (arbejdsplads, personale, forening mv.)
                      </option>
                      <option value="Temadag / workshop / tilpasset forløb">
                        Tilpasset forløb / temadag / workshop
                      </option>
                    </select>
                  </div>

                  {/* Pakke valg */}
                  <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                      Ønsket pakke
                    </label>
                    <select
                      name="package_choice"
                      value={formData.package_choice}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900 font-medium"
                    >
                      <option value="Basis (6.500 kr.)">Basis (6.500 kr.)</option>
                      <option value="Plus (Anbefalet - 9.500 kr.)">Plus (9.500 kr. - Anbefalet)</option>
                      <option value="Skolepakken (14.500 kr.)">Skolepakken (14.500 kr.)</option>
                      <option value="Tilpasset forløb / endnu ikke afklaret">Tilpasset forløb / rådgiv mig</option>
                    </select>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        Skolens / Organisationens navn *
                      </label>
                      <input
                        type="text"
                        name="school_name"
                        required
                        value={formData.school_name}
                        onChange={handleChange}
                        placeholder="F.eks. Bakkeskolen el. Virksomhed A/S"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        Kontaktperson *
                      </label>
                      <input
                        type="text"
                        name="contact_name"
                        required
                        value={formData.contact_name}
                        onChange={handleChange}
                        placeholder="F.eks. Lotte Hansen"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        E-mail *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="skole@kommune.dk"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        Telefonnummer *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Tlf. nummer"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        Klassetrin / Årgang
                      </label>
                      <input
                        type="text"
                        name="grade_levels"
                        value={formData.grade_levels}
                        onChange={handleChange}
                        placeholder="F.eks. 8. og 9. klasse / 4. årgang"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                        Forventet antal elever / deltagere
                      </label>
                      <input
                        type="text"
                        name="student_count"
                        value={formData.student_count}
                        onChange={handleChange}
                        placeholder="F.eks. ca. 75 deltagere"
                        className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                      Ønsket periode eller ca. dato
                    </label>
                    <input
                      type="text"
                      name="preferred_period"
                      value={formData.preferred_period}
                      onChange={handleChange}
                      placeholder="F.eks. Forår 2026, uge 14 eller specifik dato"
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
                      Besked eller særlige ønsker
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Fortæl gerne lidt om jeres skole, årgang, eventuelle temaer I arbejder med, eller spørgsmål..."
                      className="w-full px-4 py-3 bg-zinc-50 border border-zinc-200 focus:bg-white focus:outline-none focus:border-emerald-800 text-zinc-900"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-900 text-white py-4 font-bold text-lg hover:bg-emerald-950 transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 cursor-pointer"
                  >
                    {isSubmitting ? (
                      'Sender forespørgsel...'
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send forespørgsel
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Lightbox / Modal for flyers (Både elever og forældre) */}
      {activeModalPoster && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setActiveModalPoster(null)}
        >
          <div 
            className="relative bg-zinc-900 text-white max-w-3xl w-full p-4 sm:p-6 shadow-2xl my-8 border border-zinc-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between pb-4 border-b border-zinc-800 mb-4">
              <div>
                <h4 className="font-bold text-lg text-white">
                  {activeModalPoster === 'elever'
                    ? 'Skærmen, hjernen & trivslen'
                    : 'Hjernen, barnet & mobilen'}
                </h4>
                <p className="text-xs text-zinc-400">
                  {activeModalPoster === 'elever'
                    ? 'Elevoplæg for udskolingen (7. – 10. klasse)'
                    : 'Forældreoplæg for 3. – 6. klasse (Når mobilen flytter ind)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={activeModalPoster === 'elever' ? flyerEleverUrl : flyerForaeldreUrl}
                  download={activeModalPoster === 'elever' ? "Skaermen-hjernen-og-trivslen.png" : "Hjernen-barnet-og-mobilen.png"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-emerald-900 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 transition-colors"
                >
                  <FileDown className="w-4 h-4" />
                  <span>Download</span>
                </a>
                <button
                  type="button"
                  onClick={() => setActiveModalPoster(null)}
                  className="p-1.5 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors cursor-pointer"
                  aria-label="Luk modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="max-h-[75vh] overflow-y-auto bg-zinc-950 flex justify-center p-2">
              <img
                src={activeModalPoster === 'elever' ? flyerEleverUrl : flyerForaeldreUrl}
                alt={activeModalPoster === 'elever' ? 'Skærmen, hjernen & trivslen flyer' : 'Hjernen, barnet & mobilen flyer'}
                className="max-w-full h-auto object-contain shadow-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkaermenHjernenOgTrivselen;
